import {
  core_v1_Schedule,
  core_v1_Transform,
} from '@antimatterhq/asl-api-ts-v1';
import { formatDurationString } from '@antimatterhq/ui/utils/format';
import { produce, WritableDraft } from 'immer';
import {
  getStreamTablesValues,
  setStreamTablesValues,
} from '../streamTable/streamTableHelper';
import {
  StreamTablesSchemaValues,
  TransformSchemaValues,
} from '@/zod/schemas/transforms/TransformSchema';
import { Transform_StreamTable } from '@/types/api';
import { ProcessingScheduleSchemaFormValues } from '@/zod/schemas/ProcessingScheduleSchema';
import { ComputeGroupSchemaFormValues } from '@/zod/schemas/ComputeGroupSchema';

export const getScheduleInitialValues = (
  schedule: core_v1_Schedule | undefined | null,
): ProcessingScheduleSchemaFormValues => {
  const continuous = schedule?.continuous ?? false;
  const durationObject = formatDurationString(schedule?.atLeastEvery);
  const duration = durationObject?.value ?? '';
  const durationUnit = durationObject?.unit ?? 'm';
  const cron = schedule?.exactly ?? '';

  const scheduleType = continuous ? 'continuous' : cron ? 'cron' : 'duration';

  return {
    schedule: scheduleType,
    duration: scheduleType === 'duration' ? Number(duration || 10) : undefined,
    durationUnit: scheduleType === 'duration' ? durationUnit : 'm',
    cron,
  };
};

export const setScheduleFromValues = (
  values: ProcessingScheduleSchemaFormValues,
  schedule: WritableDraft<core_v1_Schedule>,
): core_v1_Schedule => {
  // eslint-disable-next-line no-param-reassign
  schedule.atLeastEvery =
    values.schedule === 'duration'
      ? values.duration
        ? `${values.duration}${values.durationUnit}`
        : undefined
      : undefined;

  // eslint-disable-next-line no-param-reassign
  schedule.exactly = values.schedule === 'cron' ? values.cron : undefined;
  // eslint-disable-next-line no-param-reassign
  schedule.continuous = values.schedule === 'continuous';
  return schedule;
};

export const getComputeResourceInitialValues = (
  schedule: core_v1_Schedule | undefined | null,
): ComputeGroupSchemaFormValues => {
  const computeResource =
    schedule?.computeGroup === 'dedicated'
      ? 'dedicated'
      : schedule?.computeGroup === 'automatic' || !schedule?.computeGroup
        ? 'automatic'
        : 'group';

  return {
    computeResource,
    computeResourceGroup:
      computeResource === 'group' ? schedule?.computeGroup : undefined,
  };
};

export const setComputeResourceFromValues = (
  values: ComputeGroupSchemaFormValues,
  schedule: WritableDraft<core_v1_Schedule>,
): core_v1_Schedule => {
  // eslint-disable-next-line no-param-reassign
  schedule.computeGroup =
    values.computeResource === 'group'
      ? values.computeResourceGroup
      : values.computeResource;
  return schedule;
};

export const getInputInitialValues = (
  tables: Transform_StreamTable[] | undefined | null,
): StreamTablesSchemaValues => {
  return {
    tables: getStreamTablesValues(tables || []),
  };
};

export const getEnabledInitialValue = (
  schedule: core_v1_Schedule | undefined | null,
): boolean => {
  return schedule ? schedule.enabled || false : true;
};

export const getTransformInitialValues = (
  transform: core_v1_Transform,
): TransformSchemaValues => {
  return {
    name: transform.metadata?.name || '',
    comment: transform.metadata?.comment || '',
    enabled: getEnabledInitialValue(transform.spec.schedule),
    outputTable: transform.spec.outputTable || '',
    filter: transform.spec.stream?.filter || '',
    sql: transform.spec.stream?.sql || '',
    ...getScheduleInitialValues(transform.spec.schedule),
    ...getComputeResourceInitialValues(transform.spec.schedule),
    ...getInputInitialValues(transform.spec.stream?.tables),
  };
};

export const setTransformFromValues = (
  values: TransformSchemaValues,
  transform: core_v1_Transform | undefined | null,
): core_v1_Transform => {
  return produce(
    transform || { spec: { schedule: { continuous: true } } },
    (draft) => {
      if (!draft.metadata) {
        draft.metadata = { name: '' };
      }

      draft.metadata.name = values.name;
      draft.metadata.comment = values.comment || undefined;

      if (!draft.spec.schedule) {
        draft.spec.schedule = {};
      }

      setScheduleFromValues(values, draft.spec.schedule);
      setComputeResourceFromValues(values, draft.spec.schedule);
      draft.spec.schedule.enabled = values.enabled;

      draft.spec.outputTable = values.outputTable;

      if (!draft.spec.stream) {
        draft.spec.stream = {};
      }

      draft.spec.stream.filter = values.filter;
      draft.spec.stream.sql = values.sql;

      draft.spec.stream.tables = values.tables.map((table) =>
        setStreamTablesValues(table),
      );
    },
  );
};
